<script setup lang="ts">
import { useHead } from '@vueuse/head';
import { storeToRefs } from 'pinia';
import { computed, onMounted, onServerPrefetch, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

import ThemedButton from '@/components/base/ThemedButton.vue';
import Steps from '@/components/checkout/Steps.vue';
import OrderSummary from '@/components/checkout/summary/OrderSummary.vue';
import { useCallback } from '@/composables/useCallback';
import { useCart } from '@/composables/useCart';
import { useCheckout } from '@/composables/useCheckout';
import { useDelivery } from '@/composables/useDelivery';
import { usePayment } from '@/composables/usePayment';
import money from '@/filters/money';
import { useCustomer } from '@/stores/customer';
import { CheckoutEvents } from '@/utils/analytics';
import { NutsRedirect } from '@/utils/exceptions';

const props = withDefaults(
  defineProps<{
    step: string;
    stepVariant?: string;
  }>(),
  {
    step: '',
    stepVariant: '',
  },
);

const store = useStore();

const router = useRouter();
const { containsAutoDelivery, lineItems, cart } = useCart(store);
const { clearCheckout, cxMode, determineEntryLocation, prepareCartForCheckout } =
  useCheckout(store);
const { standardShipments } = useDelivery(store);
const { estimatedAmountToCharge, paymentItems } = usePayment(store, ref(false));
const { customer } = storeToRefs(useCustomer());

useHead({
  bodyAttrs: {
    class: ['page-checkout'],
  },
  title: computed(() => `${props.step.charAt(0).toUpperCase() + props.step.slice(1)} — Nuts.com`),
});

onMounted(() => {
  CheckoutEvents.event({ action: 'spaCheckout', label: '' });
});

onServerPrefetch(async () => {
  const steps = ['signin', 'address', 'shipping', 'payment'];
  await prepareCartForCheckout();
  const entryLocation = determineEntryLocation();
  if (!cart.value || !entryLocation || !props.step || !steps.includes(props.step)) {
    throw new NutsRedirect('/cart');
  }
  const entryRoute = router.resolve(entryLocation);
  const { step } = entryRoute.params;
  if (steps.indexOf(props.step) > steps.indexOf(step as string)) {
    throw new NutsRedirect(entryLocation);
  }
});

const onClearCheckout = useCallback(async () => {
  await clearCheckout();
  if (props.step !== 'signin') {
    router.push('/checkout/signin');
  }
});

const panelBorderUtilities = 'md:border md:border-solid md:border-neutral-300 md:rounded-2xl';

const isTBD = computed(() => paymentItems.value.some((i) => i.amount === undefined));

const collapsed = ref(true);
const collapsibleState = computed(() =>
  collapsed.value
    ? {
        text: 'Expand',
        icon: nutshell['img/arrow-down.svg'],
        class: 'hidden md:block md:visible',
      }
    : {
        text: 'Collapse',
        icon: nutshell['img/arrow-up.svg'],
      },
);

const toggle = () => {
  collapsed.value = !collapsed.value;
};
</script>

<template>
  <div role="main" class="relative z-0 bg-neutral-100 full-page" data-test="checkout-page">
    <div class="mx-auto max-w-5xl md:pb-36 md:px-5" :class="cxMode ? 'pt-2 md:pt-10' : 'md:pt-20'">
      <ThemedButton
        v-if="cxMode"
        class="mr-4 mb-2 ml-auto md:mb-4 md:mr-0"
        theme="red"
        :isLoading="onClearCheckout.isPending"
        @click="onClearCheckout.execute"
      >
        Clear Checkout
      </ThemedButton>
      <div
        class="grid grid-cols-12 gap-0 md:gap-6"
        :class="{ 'opacity-50 pointer-events-none': onClearCheckout.isPending }"
      >
        <div
          class="flex sticky top-0 z-20 order-1 col-span-12 justify-between items-center px-4 pt-3 pb-2 w-full bg-white border-b border-solid border-nuts-neutral-100 md:hidden"
        >
          <div class="text-sm">
            <div class="font-bold">
              {{ isTBD ? 'Subtotal' : 'Total' }} {{ money(estimatedAmountToCharge) }}
            </div>
            <div>{{ lineItems.length }} Item{{ lineItems.length > 1 ? 's' : '' }}</div>
          </div>
          <div
            class="flex items-center text-xs text-black underline cursor-pointer"
            @click="toggle"
            role="button"
            tabindex="0"
          >
            {{ collapsibleState.text }}
            <img :src="collapsibleState.icon" class="ml-2 w-3 h-3 fill-current" alt />
          </div>
        </div>
        <div class="order-2 col-span-12 md:col-span-5 md:order-2">
          <OrderSummary
            v-model="collapsed"
            :containsAutoDelivery
            :lineItems
            :shipments="standardShipments"
          />
          <div class="w-full h-3 bg-nuts-neutral-100 md:hidden" />
        </div>

        <div class="order-3 col-span-12 md:col-span-7 md:order-1">
          <div
            class="py-5 h-full bg-white md:py-7"
            :class="panelBorderUtilities"
            data-test="main-section"
          >
            <Steps :currentStep="step" :customer :lineItems :stepVariant />
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-once id="acadia-usps-row-mobile" />
</template>

<style lang="scss" scoped>
.full-page {
  min-height: calc(100vh - 4rem);

  @media screen and (min-width: 768px) {
    min-height: calc(100vh - (5rem + 5rem));
  }
}
</style>
